import { useEffect, useState } from "react";
import { SmartTable } from "@/components/SmartTable";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/KpiCard";
import { PetitSelect } from "@/components/OperationBlock";
import { PayeursMasques } from "@/components/PayeursMasques";
import { useFilters } from "@/context/FilterContext";
import { api, errMsg, money } from "@/lib/api";

import { TYPES_LOCATION } from "@/lib/locations";

export const STATUTS_PAYEUR = [["pas_locataire", "Pas locataire"], ["ancien", "Était locataire"]];
const titre = (s) => (s || "").toLowerCase().replace(/(^|[\s-])\S/g, (c) => c.toUpperCase());
const initial = (p) => ({ logement: p.locataire?.logement || "", type: p.locataire?.type || (p.regulier ? "loyer" : "directe"), nom: p.locataire?.nom || titre(p.motcle), statut: null });
const choisir = (k) => (STATUTS_PAYEUR.some(([s]) => s === k) ? { statut: k, ...(k === "ancien" ? { type: "loyer" } : {}) } : { type: k, statut: null });
const modifie = (p, v) => {
  if (v?.statut === "pas_locataire") return true;
  const i = initial(p);
  return !!v?.logement && (v.statut === "ancien" || v.logement !== i.logement || v.type !== i.type || v.nom !== i.nom || !p.locataire);
};

const TypeSelect = ({ p, v, set }) => (
  <Select value={v.statut || v.type} onValueChange={(k) => set(choisir(k))}>
    <SelectTrigger className="h-9 bg-white" data-testid={`payeur-type-${p.motcle}`}><SelectValue /></SelectTrigger>
    <SelectContent>
      {TYPES_LOCATION.map(([k, lib]) => <SelectItem key={k} value={k} data-testid={`payeur-type-${p.motcle}-${k}`}>{lib}</SelectItem>)}
      <SelectSeparator />
      {STATUTS_PAYEUR.map(([k, lib]) => <SelectItem key={k} value={k} data-testid={`payeur-type-${p.motcle}-${k}`}>{lib}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Ligne = ({ p, v, set, logements }) => (
  <tr className="border-b border-slate-100" data-testid={`payeur-${p.motcle}`}>
    <td className="py-2 px-2"><p className="font-mono text-xs">{p.motcle}</p>{p.locataire && <span className="text-[10px] font-mono text-emerald-700">mémorisé</span>}
      {p.regulier && <span className="ml-1 rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-mono text-sky-700" title="Même montant versé au moins 3 mois de suite" data-testid={`payeur-regulier-${p.motcle}`}>mensuel régulier</span>}</td>
    <td className="py-2 px-2 text-right font-mono text-xs">{p.nb}</td>
    <td className="py-2 px-2 text-right font-mono text-xs">{money(p.total)}</td>
    <td className="py-2 px-2 font-mono text-xs text-slate-500">{p.dernier}</td>
    <td className="py-2 px-2"><Input value={v.nom} onChange={(e) => set({ nom: e.target.value })} className="h-8 text-xs bg-white" data-testid={`payeur-nom-${p.motcle}`} /></td>
    <td className="py-2 px-2 w-80"><TypeSelect p={p} v={v} set={set} /></td>
    <td className="py-2 px-2 w-56">{v.statut === "pas_locataire"
      ? <span className="text-xs text-slate-400" data-testid={`payeur-logement-${p.motcle}-aucun`}>Aucun logement</span>
      : <PetitSelect parImmeuble value={v.logement} options={logements} placeholder={v.statut === "ancien" ? "Ancien logement…" : "Ne pas mémoriser"} onChange={(logement) => set({ logement })} testId={`payeur-logement-${p.motcle}`} />}</td>
  </tr>
);

export const PayeursPanel = ({ version, onChange }) => {
  const { options } = useFilters();
  const [rows, setRows] = useState(null);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/payeurs").then((r) => { setRows(r.data); setVals(Object.fromEntries(r.data.map((p) => [p.motcle, initial(p)]))); }).catch((e) => toast.error(errMsg(e)));
  }, [version]);
  const visibles = (rows || []).filter((p) => !p.masque);
  const changes = visibles.filter((p) => modifie(p, vals[p.motcle]));
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/locataires/lot", { items: changes.map((p) => ({ motcle: p.motcle, ...vals[p.motcle] })) });
      toast.success(`${data.memorises} payeur(s) mémorisé(s) · ${data.masques} masqué(s) · ${data.associees} virement(s) associé(s)`);
      onChange();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Associer les payeurs à leur logement" testId="payeurs-panel" action={
      <Button size="sm" disabled={!changes.length || busy} onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="payeurs-enregistrer">
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Mémoriser ({changes.length})
      </Button>}>
      <p className="text-xs text-slate-500 mb-3">Tous les payeurs des dépôts hors Airbnb/Booking. Un payeur qui verse le même montant au moins 3 mois de suite (« mensuel régulier ») est proposé en location longue durée avec bail. Choisissez le logement de chacun (laissez « Ne pas mémoriser » pour les autres), puis « Mémoriser » : tous leurs virements, passés et futurs, sont associés d'un coup. « Pas locataire » masque le payeur sans toucher à ses virements ; « Était locataire » associe ses anciens virements au logement choisi, puis le masque.</p>
      {!rows ? <Loader2 className="h-5 w-5 animate-spin text-emerald-700" /> : visibles.length === 0 ? <p className="text-sm text-slate-500">Aucun payeur à associer.</p> : (
        <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
          <SmartTable id="payeurs" className="w-full text-sm" data-testid="payeurs-table">
            <thead className="sticky top-0 bg-white"><tr className="border-b border-slate-200 text-left text-[11px] font-mono uppercase tracking-wider text-slate-500">
              <th className="py-2 px-2">Payeur</th><th className="py-2 px-2 text-right">Virements</th><th className="py-2 px-2 text-right">Total</th><th className="py-2 px-2">Dernier</th>
              <th className="py-2 px-2">Nom affiché</th><th className="py-2 px-2">Type</th><th className="py-2 px-2">Logement</th>
            </tr></thead>
            <tbody>{visibles.map((p) => <Ligne key={p.motcle} p={p} v={vals[p.motcle] || initial(p)} logements={options.logements}
              set={(x) => setVals((s) => ({ ...s, [p.motcle]: { ...(s[p.motcle] || initial(p)), ...x } }))} />)}</tbody>
          </SmartTable>
        </div>
      )}
      {rows && <PayeursMasques rows={rows.filter((p) => p.masque)} onChange={onChange} />}
    </Panel>
  );
};
